import { NothingToShow } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { BarPanel } from '../components/barPanel/BarPanel';
import { PanelSkeleton } from '../components/panelSkeleton/PanelSkeleton';
import { SectionCard } from '../components/sectionCard/SectionCard';
import { SegmentedControl } from '../components/segmentedControl/SegmentedControl';
import { TimeSeriesPanel } from '../components/timeSeriesPanel/TimeSeriesPanel';

import { WorkerConnectionCard } from './WorkerConnectionCard';
import { WorkerEventsList } from './WorkerEventsList';
import { WorkerProcessCard } from './WorkerProcessCard';
import * as Styled from './WorkerTab.styles';
import { WorkerChartSource, WorkerTabProps } from './WorkerTab.types';
import { WORKER_STALE_AFTER_MS, formatDurationMs } from './worker.helpers';

export const WorkerTab = ({
  status,
  ageMs,
  events,
  isLoading,
  now,
  source,
  onSourceChange,
  canUsePrometheus,
  grafanaUrl,
  commandSeries,
  commandSeriesLabels,
  commandBars,
  isCommandsLoading,
  areCommandsUnavailable,
  hasPerServerCommands,
  perServerCommandSeries,
  perServerCommandBars,
  commandsP95Label,
  timelineFrom,
  timelineTo,
}: WorkerTabProps): JSX.Element => {
  const { formatMessage } = useLocale();

  const isStale = ageMs !== null && ageMs > WORKER_STALE_AFTER_MS;

  /**
   * The command panels read the Redis rollups or Prometheus, whichever the source toggle selects.
   * When the selected source is absent on this deployment they say so, instead of drawing an empty
   * chart that would read as "the worker sent no commands".
   */
  const renderCommandsUnavailable = (title: string): JSX.Element => (
    <SectionCard title={title} bodyHeight={160}>
      <NothingToShow
        text={formatMessage({
          id:
            source === 'prometheus'
              ? AppMessages['dod.retries.requiresPrometheus.detail']
              : AppMessages['dod.overview.unavailable'],
        })}
      />
    </SectionCard>
  );

  return (
    <>
      <Styled.Toolbar>
        <SegmentedControl<WorkerChartSource>
          label={formatMessage({ id: AppMessages['dod.worker.source'] })}
          value={source}
          onChange={onSourceChange}
          options={[
            { value: 'redis', label: formatMessage({ id: AppMessages['dod.worker.source.redis'] }) },
            {
              value: 'prometheus',
              label: formatMessage({ id: AppMessages['dod.worker.source.prometheus'] }),
              disabled: !canUsePrometheus,
            },
          ]}
        />

        <Styled.Spacer />

        {grafanaUrl && (
          <Styled.GrafanaLink href={grafanaUrl} target="_blank" rel="noreferrer noopener">
            {formatMessage({ id: AppMessages['dod.common.openInGrafana'] })}
          </Styled.GrafanaLink>
        )}
      </Styled.Toolbar>

      {isLoading && !status && <PanelSkeleton height={120} />}

      {!isLoading && !status && (
        <Styled.UnknownCard data-testid="dod-worker-unknown">
          <Styled.UnknownTitle>{formatMessage({ id: AppMessages['dod.worker.unknown.title'] })}</Styled.UnknownTitle>
          <span>{formatMessage({ id: AppMessages['dod.worker.unknown.detail'] })}</span>
        </Styled.UnknownCard>
      )}

      {status && (
        <>
          {isStale && (
            <Styled.StaleBanner role="status">
              {formatMessage({ id: AppMessages['dod.worker.stale'] }, { age: formatDurationMs(ageMs) })}
            </Styled.StaleBanner>
          )}

          <WorkerProcessCard status={status} ageMs={ageMs} now={now} />

          {status.connections.length === 0 ? (
            <SectionCard>
              <NothingToShow
                text={formatMessage({
                  id:
                    status.config?.persistentTls === false
                      ? AppMessages['dod.worker.noConnections.persistentTlsOff']
                      : AppMessages['dod.worker.noConnections'],
                })}
              />
            </SectionCard>
          ) : (
            status.connections.map((connection) => (
              <WorkerConnectionCard
                key={connection.rcpId}
                connection={connection}
                events={events}
                now={now}
                timelineFrom={timelineFrom}
                timelineTo={timelineTo}
                commandSeries={perServerCommandSeries(connection.rcpId)}
                commandSeriesLabels={commandSeriesLabels}
                commandBars={perServerCommandBars(connection.rcpId)}
                commandsP95Label={commandsP95Label}
                isCommandsLoading={isCommandsLoading}
                hasPerServerCommands={hasPerServerCommands}
              />
            ))
          )}
        </>
      )}

      {!hasPerServerCommands && (
        <Styled.PanelGrid>
          {areCommandsUnavailable ? (
            <>
              {renderCommandsUnavailable(formatMessage({ id: AppMessages['dod.worker.panel.commands'] }))}
              {renderCommandsUnavailable(formatMessage({ id: AppMessages['dod.worker.panel.commandsByType'] }))}
            </>
          ) : (
            <>
              <TimeSeriesPanel
                title={formatMessage({ id: AppMessages['dod.worker.panel.commands'] })}
                series={commandSeries}
                seriesLabels={commandSeriesLabels}
                area
                height={160}
                isLoading={isCommandsLoading}
                actions={<Styled.ToolbarLabel>{commandsP95Label}</Styled.ToolbarLabel>}
              />

              <BarPanel
                title={formatMessage({ id: AppMessages['dod.worker.panel.commandsByType'] })}
                data={commandBars}
                layout="horizontal"
                height={160}
                isLoading={isCommandsLoading}
              />
            </>
          )}
        </Styled.PanelGrid>
      )}

      <WorkerEventsList events={events} now={now} />
    </>
  );
};
